/**
 * Ambient Three.js background used behind secondary-page hero/utility
 * sections (cart, search, collection, 404, contact, project gallery).
 *
 * Visually this now follows the same recipe as the homepage's cinematic
 * intro: a ridged-multifractal noise field carved into a low-poly ground
 * mesh, producing a jagged mountain-ridge silhouette with a bright
 * grazing-angle rim line at the crest, sitting under a starfield. Unlike
 * the homepage, there is no scroll-jacked camera journey here - this is a
 * lightweight, always-idle backdrop that happens to read scroll position
 * passively (for a gentle parallax/dolly) the same way the previous
 * version of this file did.
 *
 * Grid resolution is deliberately much lower than the homepage scene: this
 * runs behind real page content (product grids, forms, cart lines) on
 * ordinary pages, not as the only thing on screen, so it is tuned to the
 * lowest vertex count that still reads as a convincing jagged skyline.
 */
(() => {
  const bg = document.querySelector('[data-network-bg]');
  if (!bg) return;

  const mount = bg.querySelector('[data-network-bg-canvas]');
  const utils = window.ThreeSceneUtils;

  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const isLowEnd =
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) ||
    (navigator.deviceMemory && navigator.deviceMemory <= 2);
  const reduceMotion = utils ? utils.prefersReducedMotion() : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const supportsWebGL = utils
    ? utils.supportsWebGL()
    : (() => {
        try {
          const c = document.createElement('canvas');
          return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
        } catch (e) {
          return false;
        }
      })();

  // Same graceful-degradation contract as before: reduced motion, no WebGL,
  // or a mobile + low-end device all fall back to the static CSS dot
  // pattern (.network-bg__fallback) and skip everything below.
  if (reduceMotion || !supportsWebGL || (isMobile && isLowEnd)) return;

  const loadScript = (src) =>
    utils
      ? utils.loadScript(src)
      : new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });

  const init = (THREE) => {
    let w = mount.clientWidth;
    let h = mount.clientHeight;
    if (!w || !h) return;

    const QUALITY = isMobile || isLowEnd ? 'low' : 'high';
    const dpr = utils ? utils.capDPR(2) : Math.min(window.devicePixelRatio || 1, 2);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: QUALITY === 'high',
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(48, w / h, 0.1, 400);

    // Camera stays close to the horizon (see calibration notes in the
    // vertex shader below) so the ridge reads as a skyline, not a
    // bird's-eye view of bumpy ground.
    const camBase = { x: 0, y: 7, z: 40 };
    camera.position.set(camBase.x, camBase.y, camBase.z);
    camera.lookAt(0, 1.5, -60);

    const lineColor = new THREE.Color(bg.style.getPropertyValue('--network-bg-line') || '#ffffff');
    const bgColorHex = (bg.style.getPropertyValue('--network-bg-color') || '#0b0d10').trim();
    const bgColor = new THREE.Color(bgColorHex);

    // ---------------------------------------------------------------
    // Soft circular point sprite (radial gradient), shared by the
    // starfield and the foreground bokeh motes - never the default
    // hard-edged GL square point.
    // ---------------------------------------------------------------
    const dotTexture = (() => {
      const size = 64;
      const c = document.createElement('canvas');
      c.width = size;
      c.height = size;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.4, 'rgba(255,255,255,0.55)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, size, size);
      return new THREE.CanvasTexture(c);
    })();

    // =================================================================
    // Terrain: ridged-multifractal ground mesh
    //
    // Same folding technique as assets/cinematic-intro.js's ridged(x, y)
    // (1 - |2n - 1|, squared, weighted by the previous octave), ported to
    // GLSL and evaluated per-vertex instead of baked on the CPU per
    // particle. That JS reference sums 5 octaves of value noise the same
    // way; the sum is NOT centered around 0.5 - it averages roughly 0.18
    // with peaks around 0.6, so the height formula below subtracts 0.18
    // before scaling rather than 0.5. Getting that offset wrong silently
    // pushes the whole terrain below the visible frame.
    //
    // NOTE: this GLSL lives inside a JS template literal - never use a
    // backtick inside a comment or string in here, it closes the string
    // early and throws "Unexpected identifier".
    // =================================================================
    const NOISE_GLSL = `
      float hash2(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }
      float vnoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        float a = hash2(i);
        float b = hash2(i + vec2(1.0, 0.0));
        float c = hash2(i + vec2(0.0, 1.0));
        float d = hash2(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
      }
      // Ridged multifractal: folding creates sharp ridges, squaring
      // narrows them, weighting by the previous octave concentrates
      // detail on top of the ridges instead of smoothing everything out.
      float ridged(vec2 p) {
        float amp = 0.5;
        float freq = 1.0;
        float sum = 0.0;
        float prev = 1.0;
        for (int i = 0; i < 5; i++) {
          float n = vnoise(p * freq);
          n = 1.0 - abs(2.0 * n - 1.0);
          n = n * n;
          sum += n * amp * prev;
          prev = n;
          amp *= 0.5;
          freq *= 2.03;
        }
        return sum;
      }
    `;

    const TERRAIN_VERTEX = `
      ${NOISE_GLSL}
      uniform float uTime;
      uniform float uAmp;
      uniform float uNoiseScale;
      uniform float uBreatheAmp;
      uniform float uBreatheSpeed;
      uniform float uRimPower;
      attribute float aPhase;
      varying float vRim;
      varying float vHeightT;

      // Height field, isolated so both the surface point and its
      // finite-difference neighbours (for the normal) share one formula.
      float terrainHeight(vec2 p) {
        float n = ridged(p * uNoiseScale);
        return (n - 0.18) * 40.0 * uAmp - 16.0;
      }

      void main() {
        vec3 pos = position;
        float baseHeight = terrainHeight(pos.xz);
        pos.y = baseHeight + sin(uTime * uBreatheSpeed + aPhase) * uBreatheAmp;

        // Finite-difference normal: sample the static height field a
        // small step away on each axis rather than differentiating the
        // fold analytically (not worth it for a low-poly background mesh).
        float eps = 0.6;
        float hL = terrainHeight(pos.xz - vec2(eps, 0.0));
        float hR = terrainHeight(pos.xz + vec2(eps, 0.0));
        float hD = terrainHeight(pos.xz - vec2(0.0, eps));
        float hU = terrainHeight(pos.xz + vec2(0.0, eps));
        vec3 tangentX = normalize(vec3(2.0 * eps, hR - hL, 0.0));
        vec3 tangentZ = normalize(vec3(0.0, hU - hD, 2.0 * eps));
        vec3 objectNormal = normalize(cross(tangentZ, tangentX));

        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        vec3 N = normalize(normalMatrix * objectNormal);
        vec3 viewDir = normalize(-mvPosition.xyz);

        // Grazing-angle rim: naturally strongest where the surface is
        // viewed edge-on. For a shallow-pitch camera looking across
        // rolling terrain that is exactly the horizon line, so this
        // alone produces a bright line at the ridge crest without any
        // scene-specific "where is the top" bookkeeping.
        vRim = pow(1.0 - clamp(dot(N, viewDir), 0.0, 1.0), uRimPower);
        vHeightT = clamp((baseHeight + 16.0) / 17.0, 0.0, 1.0);

        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    const TERRAIN_FRAGMENT = `
      precision mediump float;
      uniform vec3 uLineColor;
      uniform vec3 uBgColor;
      uniform float uRimIntensity;
      varying float vRim;
      varying float vHeightT;

      void main() {
        // The grazing-angle rim alone lights up any steep facet, not just
        // the true crest - on a jagged mesh like this that's most of the
        // surface. Gating it by height as well (only vertices already
        // near the top of their local peak) keeps the glow a thin line
        // right at the skyline instead of a wash across the whole body.
        float crestMask = smoothstep(0.28, 0.8, vHeightT);
        vec3 body = mix(uBgColor * 0.45, uBgColor * 0.95, vHeightT);
        vec3 col = body + uLineColor * (vRim * crestMask * uRimIntensity);
        gl_FragColor = vec4(col, 1.0);
      }
    `;

    const TERRAIN_CONFIG =
      QUALITY === 'high'
        ? { cols: 140, rows: 46, width: 170, depth: 110 }
        : { cols: 64, rows: 22, width: 150, depth: 90 };

    const terrainGeo = new THREE.PlaneGeometry(
      TERRAIN_CONFIG.width,
      TERRAIN_CONFIG.depth,
      TERRAIN_CONFIG.cols - 1,
      TERRAIN_CONFIG.rows - 1
    );
    terrainGeo.rotateX(-Math.PI / 2);
    const vertexCount = terrainGeo.attributes.position.count;
    const phases = new Float32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) phases[i] = Math.random() * Math.PI * 2;
    terrainGeo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    const terrainUniforms = {
      uTime: { value: 0 },
      uAmp: { value: 0.55 },
      uNoiseScale: { value: 0.058 },
      uBreatheAmp: { value: 0.05 },
      uBreatheSpeed: { value: 0.18 },
      uRimPower: { value: 3.0 },
      uRimIntensity: { value: 1.3 },
      uLineColor: { value: lineColor },
      uBgColor: { value: bgColor },
    };

    const terrainMaterial = new THREE.ShaderMaterial({
      uniforms: terrainUniforms,
      vertexShader: TERRAIN_VERTEX,
      fragmentShader: TERRAIN_FRAGMENT,
    });

    const terrain = new THREE.Mesh(terrainGeo, terrainMaterial);
    terrain.position.z = -55;
    scene.add(terrain);

    // ---------------------------------------------------------------
    // Shared point-sprite shader for the starfield and foreground
    // bokeh motes - same "breathing" + wrap-around drift + clamped
    // point-size machinery the previous version of this file used, just
    // trimmed down to the two populations that still make sense next to
    // a solid terrain mesh (the old mid-layer particle ridge is gone,
    // replaced by the mesh above).
    // ---------------------------------------------------------------
    const pointVertexShader = `
      attribute float aSize;
      attribute float aOpacity;
      attribute float aPhase;
      uniform float uTime;
      uniform float uPixelRatio;
      uniform float uScale;
      uniform float uMinDist;
      uniform float uDriftSpeed;
      uniform float uWrapHeight;
      varying float vOpacity;
      void main() {
        vec3 pos = position;
        if (uWrapHeight > 0.0) {
          float y = pos.y + uTime * uDriftSpeed;
          y = mod(y + uWrapHeight * 0.5, uWrapHeight) - uWrapHeight * 0.5;
          pos.y = y;
        }
        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        float breathe = 0.85 + 0.15 * sin(uTime * 0.55 + aPhase);
        vOpacity = aOpacity * breathe;
        float viewDist = max(uMinDist, -mvPosition.z);
        gl_PointSize = aSize * breathe * uPixelRatio * (uScale / viewDist);
        gl_Position = projectionMatrix * mvPosition;
      }
    `;
    const pointFragmentShader = `
      precision mediump float;
      uniform sampler2D uMap;
      uniform vec3 uColor;
      varying float vOpacity;
      void main() {
        vec4 tex = texture2D(uMap, gl_PointCoord);
        gl_FragColor = vec4(uColor, tex.a * vOpacity);
      }
    `;

    const sharedPointUniforms = {
      uTime: { value: 0 },
      uPixelRatio: { value: dpr },
      uScale: { value: 70 },
      uMap: { value: dotTexture },
      uColor: { value: lineColor },
    };

    const makePointMaterial = (driftSpeed, wrapHeight, minDist) =>
      new THREE.ShaderMaterial({
        uniforms: Object.assign({}, sharedPointUniforms, {
          uMinDist: { value: minDist },
          uDriftSpeed: { value: driftSpeed },
          uWrapHeight: { value: wrapHeight },
        }),
        vertexShader: pointVertexShader,
        fragmentShader: pointFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

    const buildPoints = (count, recordFn, driftSpeed, wrapHeight, minDist) => {
      const positions = new Float32Array(count * 3);
      const sizes = new Float32Array(count);
      const opacities = new Float32Array(count);
      const phases = new Float32Array(count);
      for (let i = 0; i < count; i++) {
        const r = recordFn(i);
        positions[i * 3] = r.x;
        positions[i * 3 + 1] = r.y;
        positions[i * 3 + 2] = r.z;
        sizes[i] = r.size;
        opacities[i] = r.opacity;
        phases[i] = Math.random() * Math.PI * 2;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute('aOpacity', new THREE.BufferAttribute(opacities, 1));
      geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
      return new THREE.Points(geo, makePointMaterial(driftSpeed, wrapHeight, minDist));
    };

    // ---- Starfield: scattered across the whole sky, independent of the
    // ridge shape, sitting behind/above the terrain.
    const starCount = QUALITY === 'high' ? 480 : 150;
    const stars = buildPoints(
      starCount,
      () => {
        const x = (Math.random() * 2 - 1) * 150;
        const y = 3 + Math.random() * 45;
        const z = -40 - Math.random() * 140;
        return { x, y, z, size: 3 + Math.random() * 3, opacity: 0.08 + Math.random() * 0.14 };
      },
      0,
      0,
      60
    );
    scene.add(stars);

    // ---- Near: a few dozen soft, blurry, slowly-upward-drifting
    // foreground motes (bokeh feel). Kept sparse on purpose - too many
    // and they blur out the terrain silhouette instead of adding
    // atmosphere.
    const moteCount = QUALITY === 'high' ? 28 : 12;
    const motes = buildPoints(
      moteCount,
      () => {
        const x = (Math.random() * 2 - 1) * 45;
        const y = (Math.random() - 0.5) * 16;
        const z = 8 + Math.random() * 10;
        return { x, y, z, size: 4 + Math.random() * 5, opacity: 0.14 + Math.random() * 0.16 };
      },
      0.18,
      16,
      18
    );
    scene.add(motes);

    // ---------------------------------------------------------------
    // Film grain: unchanged from the previous version of this file.
    // ---------------------------------------------------------------
    const grainCanvas = bg.querySelector('[data-network-bg-grain]');
    const grainCtx = grainCanvas ? grainCanvas.getContext('2d') : null;
    const GRAIN_SIZE = 96;
    let grainImageData = null;
    if (grainCtx) {
      grainCanvas.width = GRAIN_SIZE;
      grainCanvas.height = GRAIN_SIZE;
      grainImageData = grainCtx.createImageData(GRAIN_SIZE, GRAIN_SIZE);
    }
    const drawGrain = () => {
      if (!grainCtx || !grainImageData) return;
      const d = grainImageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        d[i] = v;
        d[i + 1] = v;
        d[i + 2] = v;
        d[i + 3] = 255;
      }
      grainCtx.putImageData(grainImageData, 0, 0);
    };

    // ---------------------------------------------------------------
    // Cursor comet trail: unchanged from the previous version of this
    // file - independent 2D canvas overlay, skipped on touch devices.
    // ---------------------------------------------------------------
    const trailCanvas = bg.querySelector('[data-network-bg-trail]');
    const trailCtx = trailCanvas && !window.matchMedia('(pointer: coarse)').matches ? trailCanvas.getContext('2d') : null;
    const trailParticles = [];
    const TRAIL_MAX = 90;
    let trailW = 0;
    let trailH = 0;
    const resizeTrail = () => {
      if (!trailCtx) return;
      const trailDpr = utils ? utils.capDPR(2) : Math.min(window.devicePixelRatio || 1, 2);
      trailW = mount.clientWidth;
      trailH = mount.clientHeight;
      trailCanvas.width = trailW * trailDpr;
      trailCanvas.height = trailH * trailDpr;
      trailCanvas.style.width = `${trailW}px`;
      trailCanvas.style.height = `${trailH}px`;
      trailCtx.setTransform(trailDpr, 0, 0, trailDpr, 0, 0);
    };
    const drawTrail = () => {
      if (!trailCtx || !trailW || !trailH) return;
      trailCtx.clearRect(0, 0, trailW, trailH);
      const now = performance.now();
      for (let i = trailParticles.length - 1; i >= 0; i--) {
        const p = trailParticles[i];
        const age = now - p.born;
        if (age > p.life) {
          trailParticles.splice(i, 1);
          continue;
        }
        const t = age / p.life;
        const alpha = (1 - t) * 0.8;
        const r = p.r * (1 - t * 0.55);
        trailCtx.beginPath();
        trailCtx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        trailCtx.shadowColor = 'rgba(255, 255, 255, 0.85)';
        trailCtx.shadowBlur = 8;
        trailCtx.arc(p.x, p.y, Math.max(0.1, r), 0, Math.PI * 2);
        trailCtx.fill();
      }
    };
    let lastTrailX = null;
    let lastTrailY = null;
    bg.addEventListener('pointermove', (e) => {
      if (!trailCtx) return;
      const rect = mount.getBoundingClientRect();
      const tx = e.clientX - rect.left;
      const ty = e.clientY - rect.top;
      if (lastTrailX !== null) {
        const dist = Math.hypot(tx - lastTrailX, ty - lastTrailY);
        const steps = Math.min(4, Math.max(1, Math.floor(dist / 14)));
        for (let s = 0; s < steps; s++) {
          if (trailParticles.length >= TRAIL_MAX) trailParticles.shift();
          const t = (s + 1) / steps;
          trailParticles.push({
            x: lastTrailX + (tx - lastTrailX) * t,
            y: lastTrailY + (ty - lastTrailY) * t,
            born: performance.now(),
            life: 500 + Math.random() * 250,
            r: 1 + Math.random() * 1.5,
          });
        }
      }
      lastTrailX = tx;
      lastTrailY = ty;
    });

    // ---------------------------------------------------------------
    // Scroll progress: read-only, passive - 0 while the section is at
    // the top of the viewport, 1 once it has been scrolled fully past.
    // Drives a small parallax shift + camera dolly, same pattern the
    // previous version of this file used, now applied to the terrain
    // and starfield instead of the old particle layers.
    // ---------------------------------------------------------------
    const fadeEl = bg.querySelector('[data-network-bg-fade]');
    let scrollProgress = 0;
    const readScroll = () => {
      const rect = bg.getBoundingClientRect();
      const total = Math.max(1, rect.height);
      scrollProgress = Math.max(0, Math.min(1, -rect.top / total));
      if (fadeEl) {
        const edge = Math.max(0, (Math.abs(scrollProgress - 0.5) - 0.35) / 0.15);
        fadeEl.style.opacity = String(Math.min(0.35, edge * 0.35));
      }
    };
    window.addEventListener('scroll', readScroll, { passive: true });
    readScroll();

    const TERRAIN_SHIFT = 10;
    const STAR_SHIFT = 5;
    const MOTE_SHIFT = 20;
    const DOLLY_DISTANCE = 8;

    const resize = () => {
      w = mount.clientWidth;
      h = mount.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      const newDpr = utils ? utils.capDPR(2) : Math.min(window.devicePixelRatio || 1, 2);
      sharedPointUniforms.uPixelRatio.value = newDpr;
      resizeTrail();
    };
    window.addEventListener('resize', resize);
    resizeTrail();

    let running = true;
    if (utils && utils.observeVisibility) {
      utils.observeVisibility(mount, (visible) => {
        running = visible;
      });
    } else {
      document.addEventListener('visibilitychange', () => {
        running = !document.hidden;
      });
    }

    const clock = new THREE.Clock();
    let frameCount = 0;
    const frame = () => {
      requestAnimationFrame(frame);
      if (!running) return;
      const elapsed = clock.getElapsedTime();

      terrainUniforms.uTime.value = elapsed;
      sharedPointUniforms.uTime.value = elapsed;

      terrain.position.x = -scrollProgress * TERRAIN_SHIFT;
      stars.position.x = -scrollProgress * STAR_SHIFT;
      motes.position.x = -scrollProgress * MOTE_SHIFT;

      camera.position.z = camBase.z - scrollProgress * DOLLY_DISTANCE;
      camera.position.y = camBase.y + Math.sin(elapsed * 0.25) * 0.4;
      camera.position.x = camBase.x + Math.sin(elapsed * 0.13) * 0.6;
      camera.lookAt(0, 1.5, -60);

      frameCount++;
      if (frameCount % 3 === 0) drawGrain();
      drawTrail();

      renderer.render(scene, camera);
    };
    frame();

    bg.classList.add('is-animated');
  };

  // Both this script and three-scene-utils.js are loaded with `defer`, in
  // that order (see the snippet markup), so deferred-script execution
  // order guarantees window.ThreeSceneUtils already exists here - same
  // assumption assets/cinematic-intro.js makes about its own utils tag.
  if (window.THREE) {
    init(window.THREE);
  } else {
    loadScript(bg.dataset.threeSrc)
      .then(() => init(window.THREE))
      .catch(() => {});
  }
})();
